/**
 * @file RegionMultiSelect.js
 * @description A multi-select dropdown component for selecting regions.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchRegions } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * A multi-select component for selecting regions.
 *
 * @component
 * @param {string} className - The CSS class name for the component.
 * @param {Array} selectedRegions - The currently selected regions.
 * @param {Function} onRegionsChange - The callback function to handle region selection changes.
 * @param {string} [label="Regions"] - The label for the component.
 * @param {boolean} [isRequired=false] - Indicates whether the component is required.
 * @param {boolean} resetTrigger - A trigger to reset the component.
 * @param {string} tenantId - The ID of the tenant.
 * @param {boolean} [showSelectedItemsInField=true] - Indicates whether to show the selected items in the input field.
 * @param {boolean} [showSelectionBox=true] - Indicates whether to show the selection box.
 * @returns {JSX.Element} The rendered RegionMultiSelect component.
 */
const RegionMultiSelect = ({
  className,
  selectedRegions,
  onRegionsChange,
  label = "Regions",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [regions, setRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRegions = async () => {
      try {
        setIsLoading(true);
        const data = await fetchRegions(tenantId);
        setRegions(data);
      } catch (error) {
        console.error("Could not fetch regions:", error);
        Toast.error(getText('msg_a3f57465cad7'));
      } finally {
        setIsLoading(false);
      }
    };

    loadRegions();
  }, [tenantId]);

  const handleRegionsChange = (selectedOptions) => {
    onRegionsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="region-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="region-dropdown"
        options={regions}
        optionIDField="id"
        updateOptions={handleRegionsChange}
        savedOptions={selectedRegions}
        savedOptionIDField="id"
        disabled={regions.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_039711232507')}
      />
    </div>
  );
};

export default RegionMultiSelect;
